import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDate } from "@/lib/format";
import { PageHeader, SectionCard, StickyPageHead } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const TYPE_OPTIONS = [
  { value: "question", label: "Soru" },
  { value: "bug", label: "Hata Bildirimi" },
  { value: "feature", label: "Özellik Önerisi" },
  { value: "other", label: "Diğer" },
];
const TYPE_LABEL = Object.fromEntries(TYPE_OPTIONS.map((o) => [o.value, o.label]));
const STATUS_LABEL = { investigating: "İnceleniyor", answered: "Cevaplandı" };

export default function Support() {
  const { user } = useAuth();
  const [type, setType] = useState("question");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // full ticket for dialog

  useEffect(() => {
    if (user && user.email && !email) setEmail(user.email);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/support/tickets");
      setTickets(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Talepler yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !subject.trim() || !message.trim()) {
      toast.error("Lütfen tüm alanları doldurun.");
      return;
    }
    setSending(true);
    try {
      await api.post("/support/tickets", {
        type,
        email: email.trim(),
        subject: subject.trim(),
        message: message.trim(),
      });
      setSubject("");
      setMessage("");
      toast.success("Mesajınız başarıyla gönderildi. Yanıtımız belirttiğiniz e-posta adresine gönderilecektir.");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Mesaj gönderilemedi.");
    } finally {
      setSending(false);
    }
  };

  const openTicket = async (id) => {
    try {
      const { data } = await api.get(`/support/tickets/${id}`);
      setSelected(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Talep yüklenemedi");
    }
  };

  return (
    <div className="space-y-6" data-testid="support-page">
      <StickyPageHead>
        <PageHeader
          title="Öneri / Destek"
          subtitle="Soru, hata bildirimi veya özellik önerinizi bize iletin"
        />
      </StickyPageHead>

      <SectionCard title="Destek Talebi Oluştur">
        <form onSubmit={onSubmit} className="p-5 space-y-4" data-testid="support-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs">Talep Türü</Label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger className="h-10 rounded-lg" data-testid="support-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TYPE_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs" htmlFor="support-email">E-posta Adresi</Label>
              <Input
                id="support-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                className="h-10 rounded-lg"
                data-testid="support-email-input"
              />
              <p className="text-[11px] text-slate-500">
                Lütfen aktif ve doğru bir e-posta adresi giriniz. Yanıtımız bu alana yazdığınız e-posta adresine gönderilecektir.
              </p>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs" htmlFor="support-subject">Konu</Label>
            <Input
              id="support-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              maxLength={200}
              className="h-10 rounded-lg"
              data-testid="support-subject-input"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs" htmlFor="support-message">Mesaj</Label>
            <Textarea
              id="support-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={6}
              maxLength={5000}
              className="rounded-lg"
              data-testid="support-message-input"
            />
          </div>

          <div className="pt-2 flex justify-end">
            <Button
              type="submit"
              disabled={sending}
              className="bg-slate-900 hover:bg-slate-800 rounded-lg"
              data-testid="support-submit-button"
            >
              {sending ? "Gönderiliyor..." : "Gönder"}
            </Button>
          </div>
        </form>
      </SectionCard>

      <SectionCard title="Taleplerim">
        <div className="p-5" data-testid="support-my-tickets">
          {loading ? (
            <div className="text-sm text-slate-500">Yükleniyor…</div>
          ) : tickets.length === 0 ? (
            <div className="text-sm text-slate-500">Henüz gönderilmiş bir talebiniz yok.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                    <th className="text-left py-2 pr-4">Talep Türü</th>
                    <th className="text-left py-2 pr-4">Konu</th>
                    <th className="text-left py-2 pr-4">Gönderim Tarihi</th>
                    <th className="text-left py-2">Durum</th>
                  </tr>
                </thead>
                <tbody>
                  {tickets.map((t) => (
                    <tr
                      key={t.id}
                      className="border-b border-slate-100 last:border-0 cursor-pointer hover:bg-slate-50"
                      onClick={() => openTicket(t.id)}
                      data-testid={`support-ticket-row-${t.id}`}
                    >
                      <td className="py-2.5 pr-4 text-slate-800">{TYPE_LABEL[t.type] || t.type}</td>
                      <td className="py-2.5 pr-4 text-slate-800">{t.subject || "—"}</td>
                      <td className="py-2.5 pr-4 text-slate-600">
                        {t.created_at ? fmtDate(t.created_at.slice(0, 10)) : "—"}
                      </td>
                      <td className="py-2.5">
                        <span
                          className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-medium ${
                            t.status === "answered"
                              ? "bg-emerald-100 text-emerald-800"
                              : "bg-amber-100 text-amber-800"
                          }`}
                        >
                          {STATUS_LABEL[t.status] || t.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </SectionCard>

      <Dialog open={!!selected} onOpenChange={(o) => { if (!o) setSelected(null); }}>
        <DialogContent className="max-w-lg" data-testid="support-ticket-dialog">
          <DialogHeader>
            <DialogTitle>{selected?.subject || "Talep"}</DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              {selected ? (
                <>
                  {TYPE_LABEL[selected.type] || selected.type} ·{" "}
                  {selected.created_at ? fmtDate(selected.created_at.slice(0, 10)) : "—"} ·{" "}
                  {STATUS_LABEL[selected.status] || selected.status}
                </>
              ) : null}
            </DialogDescription>
          </DialogHeader>
          <div
            className="text-sm text-slate-800 whitespace-pre-wrap max-h-80 overflow-y-auto pr-1"
            data-testid="support-ticket-message"
          >
            {selected?.message || ""}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
